import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { CSSProperties } from 'react'
import { apiJson, ApiError } from '../api'
import type { ThemeTokens } from '../theme'
import { pageStyles } from '../styles'
import { DataTable } from '../components/DataTable'
import type { DataTableColumn } from '../components/DataTable'
import { StatCard } from '../components/StatCard'
import { DirectoryPicker, dirEmail, dirLabel } from '../components/DirectoryPicker'
import type { DirectoryUser } from '../components/DirectoryPicker'
import { CAPABILITY_GROUPS } from '../constants'
import type { CapabilityGroup } from '../constants'
import { PersonView } from './PersonView'

/**
 * The people console: one page where a person's ACCESS and their COST are
 * administered together.
 *
 * These were four pages. Onboarding was on Users, capability groups on Users,
 * keys on Keys, chat ceilings on Budgets — so provisioning one developer meant
 * visiting all four and holding the joins in your head. Worse, nothing showed
 * that a person has TWO ways to spend, which is how developer-key spend came
 * to be invisible on every people-facing surface.
 *
 * TWO SPEND PATHS, SHOWN SIDE BY SIDE, NEVER ADDED INTO ONE CEILING.
 *
 *   chat       LibreChat's shared gateway key forwards identity in
 *              `X-LibreChat-User`; the ceiling is a LiteLLM customer record on
 *              the same email, defaulting to max_end_user_budget.
 *   developer  their own virtual key, one per person, with its own ceiling.
 *
 * They are deliberately not summed. A single merged ceiling would mean a heavy
 * Claude Code day cuts someone off mid-conversation in chat, for an unrelated
 * reason, with an error that cannot explain itself. Combined for reading,
 * separate for enforcement — and the column headers say which is which, so the
 * two numbers are never mistaken for a total.
 *
 * ROUND 13, THE IA CONSOLIDATION: `PersonView` (Access.tsx's own person
 * screen, live MCP + cached agents + the disagreement band) is now a SECOND
 * tab here, "Identity & Access", beside this page's existing "Keys &
 * Budgets" content -- the same component, unchanged, just given a second
 * home rather than living only behind Access's resource-centred matrix.
 * Access.tsx itself is untouched: "who can reach server X" stays there,
 * since collapsing it into a per-person page would lose the only place that
 * answers that question. Department, group membership and disabled status
 * are merged in from `/api/users/onboarded` and `/api/users/disabled`
 * (Graph-backed, keyed by lowercased mail/UPN, same join key `/api/people`
 * already uses) purely for FILTERING the list; the money figures never
 * cross that join, only `/api/people`'s own ledger-backed numbers.
 */

interface DeveloperKey {
  alias: string | null
  token: string | null
  spend: number
  maxBudget: number | null
  blocked: boolean
}

interface ServiceKey {
  alias: string | null
  token: string | null
  spend: number
  maxBudget: number | null
}

/** Matches `GET /api/people`. */
interface Person {
  id: string
  displayName?: string
  email: string
  department: string | null
  developerKey: DeveloperKey | null
  serviceKeys: ServiceKey[]
  chatSpend: number
  /** The LiteLLM end-user ceiling this person is enforced against. The list
   *  route already returns it beside the spend (peopleConsole.ts), so the
   *  column can show `spend / ceiling` the way Key spend does without a
   *  second request per row. `chatBudgetIsDefault` says the person has no
   *  explicit cap and is falling back to the platform default, which reads
   *  differently to a ceiling somebody chose for them. */
  chatBudget?: number | null
  chatBudgetIsDefault?: boolean
}

interface PeopleResponse {
  people: Person[]
  defaultChatBudget: number
}

/** Matches `GET /api/people/:email/chat-budget`. */
interface ChatBudget {
  email: string
  chatBudget: number
  chatBudgetIsDefault: boolean
  /** e.g. "30d". Null means the ceiling NEVER RESETS -- a lifetime cap, which
   *  is the defect that blocked a user outright and is worth saying out loud
   *  rather than leaving indistinguishable from a monthly one. */
  budgetDuration?: string | null
  budgetResetAt?: string | null
}

/** Matches `GET /api/dash/user`. Postgres `numeric`/`bigint` columns travel
 *  as strings over JSON, coerced at render time rather than trusted. */
interface PersonUsage {
  email: string
  keyOwnersAvailable: boolean
  totals: { spend: number | string; tokens: number | string; calls: number | string }
  byModel: Array<{ model: string; spend: number | string; tokens: number | string; calls: number | string }>
  daily: Array<{ day: string; spend: number | string; tokens: number | string }>
}

/** Matches `GET /api/onboarding/person`. One entry per batch this person
 *  was ever part of, most recent first. */
interface PersonOnboardingHistory {
  email: string
  batches: Array<{
    batch: { id: number; created_by: string; created_at: string; status: string; correlation_id: string | null }
    capabilityGroups: string[]
    grantedResources: string[]
    emailSentAt: string | null
    steps: Array<{ step: string; detail: string; status: string; error: string | null; ran_at: string }>
  }>
}

/** One row of `GET /api/audit`, the same shape `AuditLog.tsx` reads. */
interface PersonAuditRow {
  id: number
  operator_email: string
  action: string
  target: string | null
  result: string | null
  ts: string
}

/** Matches `GET /api/users/:id/membership` (Unit 16: folded in from the
 *  retired Users page, same endpoint, same shape). */
interface Membership {
  gate: boolean
  groups: CapabilityGroup[]
}

/** Matches `PUT /api/users/:id/groups`. */
interface ReconcileResult {
  ok: boolean
  added: string[]
  removed: string[]
  warnings: string[]
}

const money = (n: number) => `$${n.toFixed(2)}`
/** `2026-09-20T00:00:00Z` -> `2026-09-20`. Every date this page shows is a
 *  when-did-this-happen fact, never a local-timezone display, so slicing
 *  the ISO string is exact where `toLocaleDateString()` would silently
 *  shift a date near midnight. */
const fmtDate = (iso: string) => iso.slice(0, 10)

/** A short, readable result for a policy notice send. Graph's raw error
 *  bodies used to be printed in full, one per person (25 Sep 2026). */
function noticeSummary(sent: number, failed: Array<{ email: string; error?: string }>, skippedRecent = 0): string {
  const people = (n: number) => `${n} ${n === 1 ? 'person' : 'people'}`
  const skipped = skippedRecent > 0 ? ` Skipped ${people(skippedRecent)} already emailed in the last 24 hours.` : ''
  if (failed.length === 0) return `Sent to ${people(sent)}.${skipped}`
  const throttled = failed.filter((f) => /\b429\b|Throttl/i.test(f.error ?? '')).length
  const why =
    throttled === failed.length
      ? 'The mail server was busy (Microsoft throttled the sends).'
      : throttled > 0
        ? `${throttled} because the mail server was busy; the rest for other reasons.`
        : 'See the audit log for each reason.'
  return `Sent to ${sent}. ${failed.length} could not be sent: ${failed.map((f) => f.email).join(', ')}. ${why}${skipped}`
}

function retryListFromUrl(): string[] {
  try {
    const raw = new URLSearchParams(window.location.search).get('noticeRetry')
    if (!raw) return []
    return [...new Set(raw.split(',').map((e) => e.trim().toLowerCase()).filter((e) => /^[^@\s]+@[^@\s]+$/.test(e)))]
  } catch {
    return []
  }
}

export function PeoplePage({ tokens }: { tokens: ThemeTokens }) {
  const styles = useMemo(() => pageStyles(tokens), [tokens])

  const [people, setPeople] = useState<Person[]>([])
  const [defaultChatBudget, setDefaultChatBudget] = useState(100)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [selected, setSelected] = useState<Person | null>(null)
  const [chatBudget, setChatBudget] = useState<ChatBudget | null>(null)
  const [busy, setBusy] = useState(false)
  const [notice, setNotice] = useState<string | null>(null)
  /** Shown exactly once, on mint. Never re-fetchable, so it is never stored. */
  const [mintedKey, setMintedKey] = useState<string | null>(null)

  /** Staged for the grant-access control: chosen from the directory, not yet
   *  submitted, so several people can be added in one go. */
  const [pending, setPending] = useState<DirectoryUser[]>([])
  const [grantGroups, setGrantGroups] = useState<CapabilityGroup[]>([])

  /**
   * "Not started" = has access, has never spent on either path.
   *
   * Filtered from data this page already holds rather than calling
   * /api/users/never-used, which answers the same question for automation and
   * would be a second round trip and a second definition to keep in step.
   *
   * This list lives HERE and not on the staff arcade on purpose. That board's
   * stated rule is "everyone who logs a chat moves up - no bottom call-out",
   * and a named list of colleagues who have not started is exactly the bottom
   * call-out it exists to prevent. This is an outreach list for whoever drives
   * adoption, on an admin-only page.
   */
  const [onlyNotStarted, setOnlyNotStarted] = useState(false)

  /**
   * Sorted and filtered in the page, not in DataTable: that component is
   * deliberately plain (its own comment: "no virtualization, no client-side
   * sort/paginate"), and Users.tsx already sorts its rows the same way.
   *
   * Default is chat spend DESCENDING rather than alphabetical. Of 44 people
   * most sit at $0.00 with no key, so a name-ordered list buries the handful
   * anyone actually needs to act on -- and this page exists to find those.
   */
  const [sortKey, setSortKey] = useState<'name' | 'department' | 'chat' | 'key'>('chat')
  const [sortAsc, setSortAsc] = useState(false)
  const [query, setQuery] = useState('')

  /** Which person's revoke is awaiting confirmation. Revoking a key is not
   *  reversible and breaks whatever is using it, so it asks first; blocking,
   *  which is reversible, does not. */
  const [confirmRevoke, setConfirmRevoke] = useState<string | null>(null)

  /** Which person's offboard is awaiting confirmation (Unit 16). An inline
   *  row beside the tab bar, not `window.confirm` -- same reasoning as
   *  `confirmRevoke` above: this is not reversible, so it asks first, but a
   *  native dialog cannot carry the same styling or be driven by tests the
   *  way this page's other confirms already are. */
  const [confirmOffboard, setConfirmOffboard] = useState<string | null>(null)

  /** Capability-group membership for the currently open person's Identity &
   *  access tab (Unit 16, folded in from the retired Users page's "Edit
   *  groups" modal). Fetched unconditionally by `open`, below, NOT lazily
   *  on first tab visit the way `usage`/`history` are -- Identity & access
   *  is the default tab, already on screen the moment a row opens, so a
   *  click-triggered fetch would never fire unless someone happened to
   *  click a tab that was already selected.
   *
   *  Deliberately carries NO gate checkbox any more: the old modal let
   *  unticking CHU-GPT-Users double as a second way to offboard someone,
   *  with its own separate confirm wording. Offboard (above) is now the
   *  one place that removes the sign-in gate; this editor only ever sends
   *  `gate: true`, so it can add or drop a capability without touching
   *  whether the person can sign in at all. */
  const [membership, setMembership] = useState<Membership | null>(null)
  const [membershipLoading, setMembershipLoading] = useState(false)
  const [membershipError, setMembershipError] = useState<string | null>(null)
  const [editGroups, setEditGroups] = useState<Set<CapabilityGroup>>(new Set())
  const [editSaving, setEditSaving] = useState(false)
  const [editError, setEditError] = useState<string | null>(null)
  const [editResult, setEditResult] = useState<ReconcileResult | null>(null)
  // Monotonic token so a slow membership fetch for a since-closed / since-
  // switched person never lands its state on someone else, same pattern
  // `PersonView`'s own `email` effect and the retired Users page both used.
  const editReqRef = useRef(0)

  /** Which tab the expanded person panel shows. Reset to 'access' whenever a
   *  DIFFERENT person is opened (in `open`, below) so switching people never
   *  leaves you on a tab that happened to be open for someone else. */
  const [personTab, setPersonTab] = useState<'access' | 'keys' | 'usage' | 'history' | 'audit'>('access')

  /** Fetched lazily, only once the Usage tab is actually opened for this
   *  person -- the same rule the staff portal's own Developer Key tab
   *  already follows for its own detail read, so opening a row for Keys &
   *  budgets does not pay for a usage query nobody asked to see. Cleared on
   *  every new person so a stale total is never shown under the wrong name
   *  while the new one loads. */
  const [usage, setUsage] = useState<PersonUsage | null>(null)
  const [usageLoading, setUsageLoading] = useState(false)
  const [usageError, setUsageError] = useState<string | null>(null)

  /** Lazy-loaded the same way as `usage`, above -- see that state's own
   *  comment for the rule. */
  const [history, setHistory] = useState<PersonOnboardingHistory | null>(null)
  const [historyLoading, setHistoryLoading] = useState(false)
  const [historyError, setHistoryError] = useState<string | null>(null)

  const [audit, setAudit] = useState<PersonAuditRow[] | null>(null)
  const [auditLoading, setAuditLoading] = useState(false)
  const [auditError, setAuditError] = useState<string | null>(null)

  /** Capability-group badges and disabled status, keyed by lowercased
   *  mail/UPN -- purely descriptive, for filtering the list. Neither ever
   *  feeds a money figure; those come from `/api/people` alone. A failed
   *  Graph read degrades to an empty map (no groups shown, nobody flagged
   *  disabled) rather than breaking the page people actually need to
   *  administer keys and budgets from. */
  const [groupsByEmail, setGroupsByEmail] = useState<Map<string, CapabilityGroup[]>>(new Map())
  const [disabledEmails, setDisabledEmails] = useState<Set<string>>(new Set())

  const [departmentFilter, setDepartmentFilter] = useState<string>('')
  const [onlyWithKey, setOnlyWithKey] = useState(false)
  const [onlyOverBudget, setOnlyOverBudget] = useState(false)
  const [onlyDisabled, setOnlyDisabled] = useState(false)

  /** Most recent acceptance per person (Round 14, Unit 10), keyed by
   *  lowercased email -- the same "one fetch, join client-side" shape
   *  `groupsByEmail` above already uses, not a query per row. Degrades to
   *  an empty map on a failed read: "policy status unknown" reads as no
   *  badge rather than a broken page, the same posture `groupsByEmail`
   *  and `disabledEmails` already take on their own failures. */
  const [policyByEmail, setPolicyByEmail] = useState<Map<string, { policyVersion: string; acceptedAt: string }>>(
    new Map(),
  )
  const [policyCurrentVersion, setPolicyCurrentVersion] = useState<string | null>(null)
  const [policyFilter, setPolicyFilter] = useState<'any' | 'current' | 'stale' | 'none'>('any')
  const [noticeBusy, setNoticeBusy] = useState(false)
  const [noticeResult, setNoticeResult] = useState<string | null>(() =>
    retryListFromUrl().length > 0
      ? `${retryListFromUrl().length} people from an earlier policy notice send still need it.`
      : null,
  )
  /** Who the last send could not reach: a retry goes to them alone, so the
   *  people who already got it are never emailed twice (25 Sep 2026). */
  // A link can carry a retry list (?noticeRetry=a@x,b@y), so a send whose
  // failures were only on screen can be finished after a reload. Nothing
  // is sent until the button is pressed, and the server narrows the list to
  // gate members who still have not accepted.
  const [noticeFailed, setNoticeFailed] = useState<string[]>(() => retryListFromUrl())
  /** Whether the confirm step is open. The button used to POST on its own
   *  first click -- one click emailed everyone without acceptance -- so
   *  this now gates the send behind naming the live count first. */
  const [noticeConfirming, setNoticeConfirming] = useState(false)
  /** The live "who would this get emailed" count -- fetched fresh the
   *  moment the confirm opens, same posture as Settings.tsx's own
   *  `policyGateImpact` (`GET /api/settings/policy-gate/impact`, the SAME
   *  endpoint, so this count and that page's can never disagree). `null`
   *  means "not fetched yet / still fetching". */
  const [noticeImpact, setNoticeImpact] = useState<number | null>(null)
  const [noticeImpactError, setNoticeImpactError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [data, onboarded, disabled, policy] = await Promise.all([
        apiJson<PeopleResponse>('/api/people'),
        apiJson<{ users: Array<{ mail: string | null; userPrincipalName: string | null; groups: CapabilityGroup[] }> }>(
          '/api/users/onboarded',
        ).catch(() => ({ users: [] })),
        apiJson<{ disabled: Array<{ mail: string | null }> }>('/api/users/disabled').catch(() => ({ disabled: [] })),
        apiJson<{ currentVersion: string; acceptances: Array<{ email: string; policyVersion: string; acceptedAt: string }> }>(
          '/api/policy/summary',
        ).catch(() => ({ currentVersion: null, acceptances: [] })),
      ])
      const groups = new Map<string, CapabilityGroup[]>()
      for (const u of onboarded.users) {
        const email = (u.mail ?? u.userPrincipalName ?? '').toLowerCase()
        if (email) groups.set(email, u.groups)
      }
      setGroupsByEmail(groups)
      setDisabledEmails(new Set(disabled.disabled.map((d) => (d.mail ?? '').toLowerCase()).filter(Boolean)))
      setPolicyCurrentVersion(policy.currentVersion)
      setPolicyByEmail(
        new Map(policy.acceptances.map((a) => [a.email.toLowerCase(), { policyVersion: a.policyVersion, acceptedAt: a.acceptedAt }])),
      )
      setPeople(data.people)
      setDefaultChatBudget(data.defaultChatBudget)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load people.')
    } finally {
      setLoading(false)
    }
  }, [])

  /** First click: opens the confirm and fetches the LIVE count from
   *  `GET /api/settings/policy-gate/impact` (Settings.tsx's own endpoint --
   *  one computation, `unacceptedGateMembers` on the backend, so this count
   *  and the actual send list can never disagree). Never cached: a stale
   *  count in front of a one-click "email everyone" action is exactly the
   *  kind of confirm that confirms nothing. */
  const openNoticeConfirm = useCallback(async () => {
    setNoticeResult(null)
    setNoticeConfirming(true)
    setNoticeImpact(null)
    setNoticeImpactError(null)
    try {
      const data = await apiJson<{ count: number }>('/api/settings/policy-gate/impact')
      setNoticeImpact(data.count)
    } catch (err) {
      setNoticeImpactError(err instanceof ApiError ? err.message : 'Could not check who would be emailed.')
    }
  }, [])

  const cancelNoticeConfirm = useCallback(() => {
    setNoticeConfirming(false)
    setNoticeImpact(null)
    setNoticeImpactError(null)
  }, [])

  /** "Send policy notice to everyone without acceptance." Recipients are
   *  computed server-side (`POST /api/policy/notice`), never from this
   *  page's own `policyByEmail` map -- that map is a point-in-time read,
   *  and the actual send list must be current at send time, not at the
   *  page's last load or the confirm's own count a moment earlier.
   *
   * Only reachable through the confirm above (James Makin's button used to
   * POST on the very first click, emailing everyone in one press): Send is
   * this function, Cancel is `cancelNoticeConfirm`, and nothing else calls
   * this route.
   */
  const sendPolicyNotice = useCallback(async (only?: string[]) => {
    setNoticeBusy(true)
    setNoticeResult(null)
    try {
      const result = await apiJson<{
        sent: number
        failed: Array<{ email: string; error?: string }>
        skippedRecent?: number
      }>(
        '/api/policy/notice',
        only
          ? { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ emails: only }) }
          : { method: 'POST' },
      )
      setNoticeFailed(result.failed.map((f) => f.email))
      setNoticeResult(noticeSummary(result.sent, result.failed, result.skippedRecent ?? 0))
      await load()
    } catch (err) {
      setNoticeResult(err instanceof ApiError ? err.message : 'Could not send the policy notice.')
    } finally {
      setNoticeBusy(false)
      setNoticeConfirming(false)
      setNoticeImpact(null)
      setNoticeImpactError(null)
    }
  }, [load])

  useEffect(() => {
    void load()
  }, [load])

  /** The chat ceiling needs a gateway call per person, so it is fetched only
   *  when a person is actually opened rather than for every row. */
  const open = useCallback(async (person: Person) => {
    // Clicking the open person closes it: with the panel now inline, the row
    // is the toggle, and needing to hunt for a Close button to collapse the
    // thing you just opened is the friction this replaced.
    //
    // `reopening` is read from `selected` directly (a dependency of this
    // callback) rather than from a variable mutated inside `setSelected`'s
    // updater: that pattern relies on React's eager-bailout optimisation
    // invoking the updater synchronously, which is an internal timing
    // React does not guarantee, and it silently stopped firing once this
    // page gained a second caller of `open` (the `?person=` deep-link
    // effect below) -- the URL never cleared on a closing click, which the
    // deep-link effect then read back and reopened the row it was told to
    // close. Found by testing the close-then-clear behaviour directly, not
    // by reading React's docs on when updaters run.
    const reopening = selected?.email === person.email
    setSelected(reopening ? null : person)
    // Same convention as Access.tsx's ?person= handling: kept in the URL,
    // via replaceState rather than a router push, so Access's own principal
    // cells and Keys' developer-key rows can link straight to a person here,
    // and so this page can be pasted to a colleague. Cleared on collapse.
    const url = new URL(window.location.href)
    if (reopening) {
      url.searchParams.delete('person')
    } else {
      url.searchParams.set('person', person.email)
    }
    window.history.replaceState(null, '', url.toString())
    if (reopening) return
    setPersonTab('access')
    setMintedKey(null)
    setNotice(null)
    setChatBudget(null)
    setUsage(null)
    setUsageError(null)
    setHistory(null)
    setHistoryError(null)
    setAudit(null)
    setAuditError(null)
    setConfirmOffboard(null)
    editReqRef.current += 1
    setMembership(null)
    setMembershipError(null)
    setEditGroups(new Set())
    setEditError(null)
    setEditResult(null)
    try {
      setChatBudget(
        await apiJson<ChatBudget>(`/api/people/${encodeURIComponent(person.email)}/chat-budget`),
      )
    } catch {
      // A ceiling we cannot read is left null and rendered as unknown. It is
      // NOT rendered as the default: guessing here would state a limit that
      // may not be the one in force.
      setChatBudget(null)
    }
    // Membership loads unconditionally here rather than lazily on first tab
    // visit (unlike Usage/History/Audit, below): Identity & access is the
    // DEFAULT tab, already on screen the moment the row opens, so "lazy on
    // click" would mean the capability-groups editor never fetches at all
    // unless someone happens to click a tab that is already selected.
    const reqId = (editReqRef.current += 1)
    setMembershipLoading(true)
    try {
      const m = await apiJson<Membership>(`/api/users/${encodeURIComponent(person.id)}/membership`)
      if (editReqRef.current !== reqId) return
      setMembership(m)
      setEditGroups(new Set(m.groups))
    } catch (err) {
      if (editReqRef.current !== reqId) return
      setMembershipError(err instanceof ApiError ? err.message : 'Could not load capability groups.')
    } finally {
      if (editReqRef.current === reqId) setMembershipLoading(false)
    }
  }, [selected])

  function toggleEditGroup(group: CapabilityGroup) {
    setEditGroups((prev) => {
      const next = new Set(prev)
      if (next.has(group)) {
        next.delete(group)
      } else {
        next.add(group)
      }
      return next
    })
  }

  const saveGroups = useCallback(
    async (person: Person) => {
      setEditSaving(true)
      setEditError(null)
      setEditResult(null)
      try {
        // Always `gate: true` -- this editor never runs for someone who is
        // not already onboarded, and the sign-in gate itself is Offboard's
        // job now (see the state doc comment above).
        const result = await apiJson<ReconcileResult>(`/api/users/${encodeURIComponent(person.id)}/groups`, {
          method: 'PUT',
          body: JSON.stringify({ gate: true, groups: [...editGroups] }),
        })
        setEditResult(result)
        setMembership({ gate: true, groups: [...editGroups] })
        await load()
      } catch (err) {
        setEditError(err instanceof ApiError ? err.message : 'Could not save capability groups.')
      } finally {
        setEditSaving(false)
      }
    },
    [editGroups, load],
  )

  /** Removes someone from CHU-GPT-Users and every capability group in one
   *  call (Unit 16, folded in from the retired Users page's row action --
   *  same endpoint, same effect, now reached from the person panel it was
   *  previously impossible to find). Not reversible, so the confirm is
   *  asked inline beside the tab bar (`confirmOffboard`), never a native
   *  `window.confirm`. */
  const offboardPerson = useCallback(
    async (person: Person) => {
      setBusy(true)
      setNotice(null)
      try {
        await apiJson('/api/users/offboard', {
          method: 'POST',
          body: JSON.stringify({ email: person.email }),
        })
        setConfirmOffboard(null)
        setSelected(null)
        // Clears `?person=`, the same way closing a row does in `open` above --
        // otherwise the `?person=` deep-link effect reads it straight back
        // after `load()` repopulates the list and reopens the person this
        // action just closed the panel on.
        const url = new URL(window.location.href)
        url.searchParams.delete('person')
        window.history.replaceState(null, '', url.toString())
        setNotice(`Offboarded ${person.email}. They are removed from CHU-GPT-Users and every capability group.`)
        await load()
      } catch (err) {
        setNotice(err instanceof ApiError ? err.message : 'Could not offboard this person.')
      } finally {
        setBusy(false)
      }
    },
    [load],
  )

  /** Fetches on first visit to the Usage tab, not on every row open --
   *  `usage` is only ever cleared (never re-fetched) by `open`, above, so
   *  revisiting the tab for the SAME person re-uses what is already there. */
  const openUsageTab = useCallback(
    async (person: Person) => {
      setPersonTab('usage')
      if (usage || usageLoading) return
      setUsageLoading(true)
      setUsageError(null)
      try {
        setUsage(await apiJson<PersonUsage>(`/api/dash/user?email=${encodeURIComponent(person.email)}&days=30`))
      } catch (err) {
        setUsageError(err instanceof ApiError ? err.message : 'Could not load usage.')
      } finally {
        setUsageLoading(false)
      }
    },
    [usage, usageLoading],
  )

  const openHistoryTab = useCallback(
    async (person: Person) => {
      setPersonTab('history')
      if (history || historyLoading) return
      setHistoryLoading(true)
      setHistoryError(null)
      try {
        setHistory(await apiJson<PersonOnboardingHistory>(`/api/onboarding/person?email=${encodeURIComponent(person.email)}`))
      } catch (err) {
        setHistoryError(err instanceof ApiError ? err.message : 'Could not load onboarding history.')
      } finally {
        setHistoryLoading(false)
      }
    },
    [history, historyLoading],
  )

  /**
   * `resource` is an ILIKE match on `admin_audit.target`, which is the
   * person's email for budget/onboarding/membership actions but the KEY
   * TOKEN for rotate/revoke/key-budget rows (`routes/keys.ts` targets the
   * token, never the owner's email) -- so this tab is real but INCOMPLETE
   * by construction, and says so rather than implying it is everything.
   */
  const openAuditTab = useCallback(
    async (person: Person) => {
      setPersonTab('audit')
      if (audit || auditLoading) return
      setAuditLoading(true)
      setAuditError(null)
      try {
        const res = await apiJson<{ entries: PersonAuditRow[] }>(
          `/api/audit?resource=${encodeURIComponent(person.email)}&limit=50`,
        )
        setAudit(res.entries)
      } catch (err) {
        setAuditError(err instanceof ApiError ? err.message : 'Could not load the audit log.')
      } finally {
        setAuditLoading(false)
      }
    },
    [audit, auditLoading],
  )

  /** Deep-link support: `?person=<email>` opens that person once the roster
   *  has loaded, the far end of Access.tsx's own `?person=` convention (and
   *  now Keys.tsx's too) -- both link a developer key or a principal cell
   *  straight to this page rather than making an operator search by hand.
   *  Runs only while nothing is selected, so it never fights a manual click,
   *  and only once the matching row actually exists in `people`. */
  useEffect(() => {
    if (selected || people.length === 0) return
    const email = new URLSearchParams(window.location.search).get('person')
    if (!email) return
    const match = people.find((p) => p.email === email.toLowerCase())
    if (match) void open(match)
  }, [people, selected, open])

  const mintKey = useCallback(
    async (person: Person, budget: number) => {
      setBusy(true)
      setNotice(null)
      try {
        const res = await apiJson<{ key: string; alias: string }>('/api/keys/dev', {
          method: 'POST',
          body: JSON.stringify({ email: person.email, budget }),
        })
        setMintedKey(res.key)
        setNotice(`Minted ${res.alias}. Copy the key now — it is never shown again.`)
        await load()
      } catch (err) {
        // The 409 from the one-key-per-person rule carries a message naming
        // the key they already hold, which is more useful than anything this
        // page could invent, so it is surfaced verbatim.
        setNotice(err instanceof ApiError ? err.message : 'Could not mint a key.')
      } finally {
        setBusy(false)
      }
    },
    [load],
  )

  const setChatCeiling = useCallback(
    async (person: Person, maxBudget: number) => {
      setBusy(true)
      setNotice(null)
      try {
        await apiJson('/api/budgets/user', {
          method: 'POST',
          body: JSON.stringify({ email: person.email, maxBudget }),
        })
        setChatBudget({ email: person.email, chatBudget: maxBudget, chatBudgetIsDefault: false })
        setNotice(`Chat ceiling for ${person.email} set to ${money(maxBudget)}.`)
      } catch (err) {
        setNotice(err instanceof ApiError ? err.message : 'Could not set the chat budget.')
      } finally {
        setBusy(false)
      }
    },
    [],
  )

  const setKeyCeiling = useCallback(
    async (person: Person, maxBudget: number) => {
      if (!person.developerKey?.token) return
      setBusy(true)
      setNotice(null)
      try {
        await apiJson(`/api/keys/${encodeURIComponent(person.developerKey.token)}/budget`, {
          method: 'POST',
          body: JSON.stringify({ maxBudget }),
        })
        setNotice(`Key ceiling for ${person.email} set to ${money(maxBudget)}.`)
        await load()
      } catch (err) {
        setNotice(err instanceof ApiError ? err.message : 'Could not set the key budget.')
      } finally {
        setBusy(false)
      }
    },
    [load],
  )

  const grantAccess = useCallback(async () => {
    setBusy(true)
    setNotice(null)
    try {
      const res = await apiJson<{ results: Array<{ email: string; ok: boolean; error?: string }> }>(
        '/api/users/onboard',
        {
          method: 'POST',
          body: JSON.stringify({
            emails: pending.map(dirEmail).filter(Boolean),
            groups: grantGroups,
          }),
        },
      )
      // The endpoint reports PER EMAIL and does not fail the batch on one bad
      // address, so the notice must too: saying "granted" when one of four
      // failed is exactly the lie this wording avoids.
      const failed = res.results.filter((r) => !r.ok)
      setNotice(
        failed.length
          ? `${res.results.length - failed.length} granted. Failed: ${failed
              .map((f) => `${f.email} (${f.error ?? 'unknown'})`)
              .join(', ')}`
          : `Access granted to ${res.results.length} ${res.results.length === 1 ? 'person' : 'people'}.`,
      )
      setPending([])
      setGrantGroups([])
      await load()
    } catch (err) {
      setNotice(err instanceof ApiError ? err.message : 'Could not grant access.')
    } finally {
      setBusy(false)
    }
  }, [pending, grantGroups, load])

  const backfillBudgets = useCallback(async () => {
    setBusy(true)
    setNotice(null)
    try {
      const res = await apiJson<{
        considered: number
        skippedWithExplicitBudget: number
        updated: number
        failed: Array<{ email: string; error?: string }>
      }>('/api/people/backfill-chat-budgets', { method: 'POST' })
      setNotice(
        `Gave ${res.updated} people a 30-day chat budget. ` +
          `${res.skippedWithExplicitBudget} already had an explicit one and were left alone.` +
          (res.failed.length
            ? ` ${res.failed.length} failed: ${res.failed.map((f) => f.email).join(', ')}.`
            : ''),
      )
      await load()
    } catch (err) {
      setNotice(err instanceof ApiError ? err.message : 'Could not backfill budgets.')
    } finally {
      setBusy(false)
    }
  }, [load])

  const revokeKey = useCallback(
    async (person: Person) => {
      const token = person.developerKey?.token
      if (!token) return
      setBusy(true)
      setNotice(null)
      try {
        await apiJson(`/api/keys/${encodeURIComponent(token)}`, { method: 'DELETE' })
        setNotice(
          `Revoked ${person.developerKey?.alias ?? 'the key'}. Anything still using it will start failing immediately.`,
        )
        setConfirmRevoke(null)
        await load()
      } catch (err) {
        setNotice(err instanceof ApiError ? err.message : 'Could not revoke the key.')
      } finally {
        setBusy(false)
      }
    },
    [load],
  )

  const toggleBlock = useCallback(
    async (person: Person) => {
      const token = person.developerKey?.token
      if (!token) return
      const blocking = !person.developerKey?.blocked
      setBusy(true)
      setNotice(null)
      try {
        await apiJson(`/api/keys/${encodeURIComponent(token)}/${blocking ? 'block' : 'unblock'}`, {
          method: 'POST',
        })
        setNotice(blocking ? 'Key blocked. Unblock restores it.' : 'Key unblocked.')
        await load()
      } catch (err) {
        setNotice(err instanceof ApiError ? err.message : 'Could not change the key.')
      } finally {
        setBusy(false)
      }
    },
    [load],
  )

  const withKeys = people.filter((p) => p.developerKey).length
  // Counted against `people` (already filtered to valid, non-synthetic
  // addresses), not `disabledEmails.size` directly -- the raw Graph count
  // can include a disabled account with no usable email, which would never
  // appear as a row here, and the checkbox label must match what it filters.
  const disabledCount = people.filter((p) => disabledEmails.has(p.email)).length
  // Never spent on EITHER path. A key with spend counts as started even
  // with no chat use: they are using the platform, just not through chat.
  const notStarted = people.filter(
    (p) => p.chatSpend === 0 && (p.developerKey?.spend ?? 0) === 0,
  )
  /** Choosing a column from the select sets its NATURAL direction; the arrow
   *  button flips it. Toggling on re-select would be surprising here, because
   *  re-picking the current option in a <select> is not a click on a header. */
  function toggleSortTo(key: 'name' | 'department' | 'chat' | 'key') {
    setSortKey(key)
    setSortAsc(key === 'name' || key === 'department')
  }

  function toggleSort(key: 'name' | 'department' | 'chat' | 'key') {
    if (key === sortKey) {
      setSortAsc((v) => !v)
    } else {
      setSortKey(key)
      // Money columns open descending (biggest first, which is what you are
      // looking for); text columns open A-Z.
      setSortAsc(key === 'name' || key === 'department')
    }
  }

  /** Distinct departments actually present, for the filter dropdown --
   *  never a hardcoded list, so a renamed or new department just appears. */
  const departments = useMemo(() => {
    const set = new Set<string>()
    for (const p of people) if (p.department) set.add(p.department)
    return [...set].sort()
  }, [people])

  /** Over the developer-KEY ceiling only -- exact, since `spend`/`maxBudget`
   *  are per-row already. Chat's ceiling is fetched per person, lazily, only
   *  once a row is opened (see `open`, above), so it cannot be checked for
   *  every row here without a fetch per person; rather than approximate off
   *  the platform default and risk flagging someone with a raised ceiling as
   *  over budget, this filter says exactly what it checks. */
  function isOverKeyBudget(p: Person): boolean {
    return p.developerKey !== null && p.developerKey.maxBudget !== null && p.developerKey.spend > p.developerKey.maxBudget
  }

  const shown = useMemo(() => {
    let base = onlyNotStarted ? notStarted : people
    if (departmentFilter) base = base.filter((p) => p.department === departmentFilter)
    if (onlyWithKey) base = base.filter((p) => p.developerKey !== null)
    if (onlyOverBudget) base = base.filter(isOverKeyBudget)
    if (onlyDisabled) base = base.filter((p) => disabledEmails.has(p.email))
    if (policyFilter !== 'any') {
      base = base.filter((p) => {
        const a = policyByEmail.get(p.email)
        if (policyFilter === 'none') return !a
        if (!a) return false
        const isCurrent = policyCurrentVersion !== null && a.policyVersion === policyCurrentVersion
        return policyFilter === 'current' ? isCurrent : !isCurrent
      })
    }

    const needle = query.trim().toLowerCase()
    const filtered = needle
      ? base.filter(
          (p) =>
            p.email.includes(needle) ||
            (p.displayName ?? '').toLowerCase().includes(needle) ||
            (p.department ?? '').toLowerCase().includes(needle),
        )
      : base

    const dir = sortAsc ? 1 : -1
    return [...filtered].sort((a, b) => {
      switch (sortKey) {
        case 'department':
          // Unassigned sorts last in BOTH directions rather than leading the
          // list as an empty string would.
          return (
            ((a.department ?? '\uffff').localeCompare(b.department ?? '\uffff') ||
              (a.displayName ?? a.email).localeCompare(b.displayName ?? b.email)) * dir
          )
        case 'chat':
          return (a.chatSpend - b.chatSpend) * dir
        case 'key':
          return ((a.developerKey?.spend ?? -1) - (b.developerKey?.spend ?? -1)) * dir
        default:
          return (a.displayName ?? a.email).localeCompare(b.displayName ?? b.email) * dir
      }
    })
  }, [
    people,
    notStarted,
    onlyNotStarted,
    departmentFilter,
    onlyWithKey,
    onlyOverBudget,
    onlyDisabled,
    disabledEmails,
    policyFilter,
    policyByEmail,
    policyCurrentVersion,
    query,
    sortKey,
    sortAsc,
  ])

  // A filter toggle drawn as a chip. Keeps checkboxRow's own gap and colour
  // (the checkbox test pins both), adds a pill outline so the four read as
  // one set.
  const filterChip: CSSProperties = {
    ...styles.checkboxRow,
    padding: '0.35rem 0.75rem',
    borderRadius: '9999px',
    border: `1px solid ${tokens.border}`,
    background: tokens.surface,
    cursor: 'pointer',
  }

  const columns: Array<DataTableColumn<Person>> = [
    {
      key: 'person',
      header: `Person${sortKey === 'name' ? (sortAsc ? ' \u2191' : ' \u2193') : ''}`,
      render: (p) => (
        <div>
          <div style={{ fontWeight: 600 }}>
            {p.displayName ?? p.email}
            {disabledEmails.has(p.email) ? (
              <span style={{ ...styles.badge, marginLeft: '0.4rem' }}>disabled</span>
            ) : null}
          </div>
          <div style={{ color: tokens.textSecondary, fontSize: '0.85em' }}>{p.email}</div>
        </div>
      ),
    },
    {
      key: 'groups',
      header: 'Groups',
      render: (p) => {
        const g = groupsByEmail.get(p.email) ?? []
        return g.length ? (
          <span title={g.join(', ')} style={{ color: tokens.textSecondary, whiteSpace: 'nowrap' }}>
            {g.length} {g.length === 1 ? 'group' : 'groups'}
          </span>
        ) : (
          <span style={{ color: tokens.textSecondary }}>{'\u2014'}</span>
        )
      },
    },
    {
      key: 'department',
      header: `Department${sortKey === 'department' ? (sortAsc ? ' \u2191' : ' \u2193') : ''}`,
      render: (p) => p.department ?? '—',
    },
    {
      // Header says "chat", not "spend". The two figures are never a total and
      // the columns must not imply that they are.
      key: 'chat',
      header: `Chat spend${sortKey === 'chat' ? (sortAsc ? ' \u2191' : ' \u2193') : ''}`,
      align: 'right',
      // Spend beside the ceiling it is enforced against (Maroun, 24 Sep
      // 2026), in the same `spend / ceiling` shape as Key spend so the two
      // columns read alike. A default ceiling is dimmed rather than hidden:
      // the number still binds, it was just nobody's decision.
      render: (p) => {
        const cap = p.chatBudget ?? defaultChatBudget
        return (
          <span style={{ whiteSpace: 'nowrap' }}>
            {money(p.chatSpend)}
            {cap !== null && cap !== undefined ? (
              <span style={p.chatBudgetIsDefault === false ? undefined : { color: tokens.textSecondary }}>
                {' / '}
                {money(cap)}
              </span>
            ) : null}
          </span>
        )
      },
    },
    {
      key: 'devKey',
      header: 'Developer key',
      render: (p) =>
        p.developerKey ? (
          <span>
            {p.developerKey.alias ?? 'unnamed'}
            {p.developerKey.blocked ? ' (blocked)' : ''}
          </span>
        ) : (
          <span style={{ color: tokens.textSecondary }}>none</span>
        ),
    },
    {
      key: 'keySpend',
      header: `Key spend${sortKey === 'key' ? (sortAsc ? ' \u2191' : ' \u2193') : ''}`,
      align: 'right',
      render: (p) =>
        p.developerKey ? (
          <span style={{ whiteSpace: 'nowrap' }}>
            {money(p.developerKey.spend)}
            {p.developerKey.maxBudget !== null ? ` / ${money(p.developerKey.maxBudget)}` : ''}
          </span>
        ) : (
          <span style={{ color: tokens.textSecondary }}>—</span>
        ),
    },
    {
      key: 'policy',
      header: 'Policy',
      render: (p) => {
        const a = policyByEmail.get(p.email)
        // A pill that never wraps (Maroun, 24 Sep 2026): "accepted
        // 2026-09-24" used to break across three lines in a narrow column.
        const pill = (text: string, bad: boolean) => (
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              padding: '0.15rem 0.6rem',
              borderRadius: '9999px',
              fontSize: '0.78rem',
              fontWeight: 600,
              whiteSpace: 'nowrap',
              background: bad ? 'rgba(179, 38, 30, 0.1)' : 'rgba(0, 168, 110, 0.12)',
              color: bad ? tokens.danger : tokens.brandHover,
            }}
          >
            {text}
          </span>
        )
        if (!a) return pill('not accepted', true)
        const stale = policyCurrentVersion !== null && a.policyVersion !== policyCurrentVersion
        return (
          <div style={{ whiteSpace: 'nowrap' }}>
            {pill(`${a.policyVersion}${stale ? ' (stale)' : ''}`, stale)}
            <div style={{ color: tokens.textSecondary, fontSize: '0.78rem', marginTop: '0.2rem' }}>
              accepted {fmtDate(a.acceptedAt)}
            </div>
          </div>
        )
      },
    },
  ]

  /**
   * The Keys & Budgets tab of the per-person panel, rendered INSIDE the
   * table beneath its row so the person being edited stays on screen.
   *
   * Uses the shared style tokens rather than bare elements: the first cut
   * shipped raw <h2>/<input>/<button> with no styles at all, which is why it
   * did not look like the rest of the panel.
   */
  const renderKeysAndBudgets = (person: Person) => (
    <div aria-label={`Manage ${person.email}`} style={{ display: 'grid', gap: '1rem' }}>
      {notice ? (
        <div style={styles.callout} role="status">
          {notice}
        </div>
      ) : null}

      {mintedKey ? (
        <div style={{ ...styles.callout, display: 'grid', gap: '0.4rem' }}>
          <strong>Copy this key now — it is shown once and never again.</strong>
          {/* Selected on focus so one click-and-copy gets the whole secret;
              a half-selected key fails silently at the other end. */}
          <input
            readOnly
            value={mintedKey}
            onFocus={(e) => e.currentTarget.select()}
            style={{ ...styles.input, fontFamily: 'ui-monospace, monospace' }}
            aria-label="Newly minted developer key"
          />
        </div>
      ) : null}

      {/* Two ceilings, two cards, side by side. The layout carries the point
          the copy keeps having to make: these are separate limits, not two
          halves of one budget. */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(19rem, 1fr))',
          gap: '1rem',
          alignItems: 'start',
        }}
      >
        <div style={{ ...styles.section, padding: '1rem' }}>
          <div style={styles.sectionTitle}>Chat</div>
          <div style={styles.sectionSubtitle}>
            Through CHU GPT itself, under a per-person LiteLLM ceiling.
          </div>
          <p style={{ margin: '0.6rem 0' }}>
            <strong>{money(person.chatSpend)}</strong>{' '}
            <span style={styles.hint}>
              of{' '}
              {chatBudget
                ? `${money(chatBudget.chatBudget)}${chatBudget.chatBudgetIsDefault ? ' (platform default)' : ''}`
                : 'an unknown ceiling'}
            </span>
          </p>
          {chatBudget ? (
            <p style={{ margin: '0.6rem 0', ...styles.hint }}>
              {chatBudget.budgetDuration
                ? `Resets every ${chatBudget.budgetDuration}${
                    chatBudget.budgetResetAt
                      ? ` — next on ${chatBudget.budgetResetAt.slice(0, 10)}`
                      : ''
                  }.`
                : 'Never resets — this is a lifetime cap, so they are refused permanently once they reach it.'}
            </p>
          ) : null}
          <BudgetForm
            styles={styles}
            label="Chat ceiling"
            action="Save"
            disabled={busy}
            initial={chatBudget?.chatBudget ?? defaultChatBudget}
            onSubmit={(v) => void setChatCeiling(person, v)}
          />
        </div>

        <div style={{ ...styles.section, padding: '1rem' }}>
          <div style={styles.sectionTitle}>Developer key</div>
          <div style={styles.sectionSubtitle}>
            Their own API key, for Claude Code and scripts. One per person.
          </div>
          {person.developerKey ? (
            <>
              <p style={{ margin: '0.6rem 0' }}>
                <span style={styles.badge}>{person.developerKey.alias}</span>{' '}
                {person.developerKey.blocked ? <span style={styles.badge}>blocked</span> : null}
              </p>
              <p style={{ margin: '0.6rem 0' }}>
                <strong>{money(person.developerKey.spend)}</strong>{' '}
                <span style={styles.hint}>
                  of{' '}
                  {person.developerKey.maxBudget !== null
                    ? money(person.developerKey.maxBudget)
                    : 'no ceiling'}
                </span>
              </p>
              <BudgetForm
                styles={styles}
                label="Key ceiling"
                action="Save"
                disabled={busy}
                initial={person.developerKey.maxBudget ?? 50}
                onSubmit={(v) => void setKeyCeiling(person, v)}
              />
              <div style={{ ...styles.toolbar, marginTop: '0.8rem' }}>
                {/* Block is reversible and is the right first move for a
                    suspected leak; revoke is not, so it asks first. */}
                <button
                  type="button"
                  style={styles.buttonGhost}
                  disabled={busy}
                  onClick={() => void toggleBlock(person)}
                >
                  {person.developerKey.blocked ? 'Unblock' : 'Block'}
                </button>
                {confirmRevoke === person.email ? (
                  <>
                    <span style={styles.hint}>Revoke permanently?</span>
                    <button
                      type="button"
                      style={styles.buttonDanger}
                      disabled={busy}
                      onClick={() => void revokeKey(person)}
                    >
                      Yes, revoke
                    </button>
                    <button
                      type="button"
                      style={styles.buttonGhost}
                      disabled={busy}
                      onClick={() => setConfirmRevoke(null)}
                    >
                      Cancel
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    style={styles.buttonDanger}
                    disabled={busy}
                    onClick={() => setConfirmRevoke(person.email)}
                  >
                    Revoke key
                  </button>
                )}
              </div>
            </>
          ) : (
            <>
              <p style={{ margin: '0.6rem 0', ...styles.hint }}>
                No key yet. Minting gives them one whose ceiling is their developer budget.
              </p>
              <BudgetForm
                styles={styles}
                label="Ceiling"
                action="Mint key"
                disabled={busy}
                initial={50}
                onSubmit={(v) => void mintKey(person, v)}
              />
            </>
          )}
        </div>
      </div>

      {person.serviceKeys.length ? (
        <div style={{ ...styles.section, padding: '1rem' }}>
          <div style={styles.sectionTitle}>Service keys they own</div>
          <div style={styles.sectionSubtitle}>
            Automation, not this person&rsquo;s usage, so never counted as their developer spend.
          </div>
          <div style={{ display: 'grid', gap: '0.35rem', marginTop: '0.6rem' }}>
            {person.serviceKeys.map((k) => (
              <div key={k.token ?? k.alias ?? 'unnamed'}>
                <span style={styles.badge}>{k.alias ?? 'unnamed'}</span>{' '}
                <span style={styles.hint}>
                  {money(k.spend)}
                  {k.maxBudget !== null ? ` of ${money(k.maxBudget)}` : ''}
                </span>
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  )

  /** The tab bar itself: "Identity & Access" (PersonView, unchanged) beside
   *  "Keys & Budgets" (this page's own long-standing content, above). One
   *  person, one panel, two jobs -- rather than two separate top-level pages
   *  an operator had to hold in their head as the same colleague. */
  /**
   * The Usage tab: this person's own `/api/dash/user` slice (last 30 days),
   * the same identity-resolved figures every other per-person surface on
   * this platform reads -- see that route's own doc comment for the
   * device-blob fix this depends on. "One person, everything about them" is
   * the point of this whole consolidation, and usage is what an operator
   * most often opens a person's page FOR, so it earns real content rather
   * than a link out to Dashboards.
   */
  const renderUsage = () => {
    if (usageLoading) {
      return <p style={styles.hint}>Loading usage…</p>
    }
    if (usageError) {
      return <p style={styles.error}>{usageError}</p>
    }
    if (!usage) {
      return <p style={styles.hint}>Select the tab again to load usage.</p>
    }
    const maxDaily = Math.max(1, ...usage.daily.map((d) => Number(d.spend)))
    return (
      <div style={{ display: 'grid', gap: '1rem' }}>
        {!usage.keyOwnersAvailable ? (
          <div style={styles.callout} role="status">
            The developer-key roster could not be read just now, so any Claude Code usage below may be
            undercounted until it can.
          </div>
        ) : null}
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
          <StatCard label="Spend, last 30 days" value={money(Number(usage.totals.spend ?? 0))} tokens={tokens} />
          <StatCard
            label="Tokens, last 30 days"
            value={Number(usage.totals.tokens ?? 0).toLocaleString()}
            tokens={tokens}
          />
          <StatCard label="Calls, last 30 days" value={Number(usage.totals.calls ?? 0).toLocaleString()} tokens={tokens} />
        </div>

        <div style={{ ...styles.section, padding: '1rem' }}>
          <div style={styles.sectionTitle}>By model</div>
          {usage.byModel.length === 0 ? (
            <p style={styles.hint}>No usage in the last 30 days.</p>
          ) : (
            <div style={{ display: 'grid', gap: '0.35rem', marginTop: '0.6rem' }}>
              {usage.byModel.map((m) => (
                <div key={m.model} style={{ display: 'flex', justifyContent: 'space-between', gap: '0.5rem' }}>
                  <span>{m.model}</span>
                  <span style={styles.hint}>
                    {money(Number(m.spend))} · {Number(m.tokens).toLocaleString()} tokens
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div style={{ ...styles.section, padding: '1rem' }}>
          <div style={styles.sectionTitle}>Daily spend</div>
          {usage.daily.length === 0 ? (
            <p style={styles.hint}>No usage in the last 30 days.</p>
          ) : (
            <div style={{ display: 'grid', gap: '0.25rem', marginTop: '0.6rem' }}>
              {usage.daily.map((d) => (
                <div key={d.day} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span style={{ ...styles.hint, width: '5.5rem', flexShrink: 0 }}>{d.day.slice(0, 10)}</span>
                  <div style={{ flex: 1, background: tokens.surfaceRaised, borderRadius: '0.2rem' }}>
                    <div
                      style={{
                        width: `${(Number(d.spend) / maxDaily) * 100}%`,
                        minWidth: Number(d.spend) > 0 ? '2px' : 0,
                        height: '0.6rem',
                        background: tokens.brand,
                        borderRadius: '0.2rem',
                      }}
                    />
                  </div>
                  <span style={{ ...styles.hint, width: '4.5rem', textAlign: 'right', flexShrink: 0 }}>
                    {money(Number(d.spend))}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    )
  }

  /** Onboarding history: every batch this person was ever part of, oldest
   *  batches collapsed to their headline (date, status, who ran it), most
   *  recent expanded to its step list -- the same shape Budget requests'
   *  own timeline already uses on the Developer key tab. */
  const renderHistory = () => {
    if (historyLoading) {
      return <p style={styles.hint}>Loading onboarding history…</p>
    }
    if (historyError) {
      return <p style={styles.error}>{historyError}</p>
    }
    if (!history) {
      return <p style={styles.hint}>Select the tab again to load onboarding history.</p>
    }
    if (history.batches.length === 0) {
      return <p style={styles.hint}>Never appeared in an onboarding batch.</p>
    }
    return (
      <div style={{ display: 'grid', gap: '0.8rem' }}>
        {history.batches.map((b, i) => (
          <div key={b.batch.id} style={{ ...styles.section, padding: '1rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.5rem' }}>
              <span>
                Batch #{b.batch.id} · {fmtDate(b.batch.created_at)} · by {b.batch.created_by}
              </span>
              <span style={styles.badge}>{b.batch.status}</span>
            </div>
            <div style={{ ...styles.hint, marginTop: '0.3rem' }}>
              Groups: {b.capabilityGroups.length ? b.capabilityGroups.join(', ') : 'none'}
              {' · '}
              Granted: {b.grantedResources.length ? b.grantedResources.join(', ') : 'none'}
              {' · '}
              Email {b.emailSentAt ? `sent ${fmtDate(b.emailSentAt)}` : 'not sent'}
            </div>
            {/* Only the most recent batch's steps are shown open -- the rest
                are one click of the summary line above away, and printing
                every step for every batch buries the one someone came for. */}
            {i === 0 && b.steps.length > 0 ? (
              <div style={{ display: 'grid', gap: '0.2rem', marginTop: '0.5rem' }}>
                {b.steps.map((s, si) => (
                  <div key={si} style={{ display: 'flex', gap: '0.5rem', fontSize: '0.84rem' }}>
                    <span style={{ color: s.status === 'error' ? tokens.danger : tokens.textSecondary }}>
                      {s.status === 'error' ? 'error' : 'ok'}
                    </span>
                    <span>{s.step}</span>
                    <span style={styles.hint}>{s.detail}</span>
                    {s.error ? <span style={{ color: tokens.danger }}>{s.error}</span> : null}
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        ))}
      </div>
    )
  }

  /** Audit: every `admin_audit` row whose `target` matches this email. Says
   *  its own limit up front rather than implying completeness -- see
   *  `openAuditTab`'s doc comment for why key-token-targeted rows (rotate,
   *  revoke, key budget) are not in this list. */
  const renderAudit = () => {
    if (auditLoading) {
      return <p style={styles.hint}>Loading audit history…</p>
    }
    if (auditError) {
      return <p style={styles.error}>{auditError}</p>
    }
    if (!audit) {
      return <p style={styles.hint}>Select the tab again to load the audit log.</p>
    }
    return (
      <div style={{ display: 'grid', gap: '0.6rem' }}>
        <p style={styles.hint}>
          Rows whose target names this email -- budget changes, onboarding, and membership edits. Developer-key
          rotate/revoke/budget rows are logged against the key, not the person, and are not shown here yet; see
          the Keys page for those.
        </p>
        {audit.length === 0 ? (
          <p style={styles.hint}>No matching audit rows.</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.86rem' }}>
              <thead>
                <tr>
                  {['When', 'Operator', 'Action', 'Result'].map((h) => (
                    <th
                      key={h}
                      style={{
                        textAlign: 'left',
                        padding: '0.4rem 0.5rem',
                        borderBottom: `1px solid ${tokens.border}`,
                        color: tokens.textSecondary,
                        fontSize: '0.72rem',
                        textTransform: 'uppercase',
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {audit.map((row) => (
                  <tr key={row.id}>
                    <td style={{ padding: '0.4rem 0.5rem', borderBottom: `1px solid ${tokens.border}` }}>{fmtDate(row.ts)}</td>
                    <td style={{ padding: '0.4rem 0.5rem', borderBottom: `1px solid ${tokens.border}` }}>{row.operator_email}</td>
                    <td style={{ padding: '0.4rem 0.5rem', borderBottom: `1px solid ${tokens.border}` }}>{row.action}</td>
                    <td
                      style={{
                        padding: '0.4rem 0.5rem',
                        borderBottom: `1px solid ${tokens.border}`,
                        color: row.result === 'ok' ? tokens.textPrimary : tokens.danger,
                      }}
                    >
                      {row.result ?? 'unknown'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    )
  }

  /** Capability groups, above `PersonView` on the Identity & access tab
   *  (Unit 16). Deliberately its own small card rather than a modal --
   *  `PersonView` already lives inline in this tab, and a second overlay on
   *  top of it is the wrong direction to keep going in. */
  const renderCapabilityGroups = (person: Person) => (
    <div style={{ ...styles.section, padding: '1rem' }}>
      <div style={styles.sectionTitle}>Capability groups</div>
      <div style={styles.sectionSubtitle}>
        What this adds on top of sign-in access. Saving adds what is ticked and removes what is not, in Entra,
        immediately. To remove sign-in access entirely, use Offboard above instead.
      </div>
      {membershipLoading ? (
        <p style={styles.hint}>Loading current membership…</p>
      ) : membershipError ? (
        <p style={styles.error}>{membershipError}</p>
      ) : !membership ? (
        <p style={styles.hint}>Open a person to load capability groups.</p>
      ) : (
        <>
          <div style={styles.checkboxGrid}>
            {CAPABILITY_GROUPS.map((group) => (
              <label key={group} style={styles.checkboxRow}>
                <input
                  type="checkbox"
                  checked={editGroups.has(group)}
                  onChange={() => toggleEditGroup(group)}
                  disabled={editSaving}
                />
                CHU-GPT-{group}
              </label>
            ))}
          </div>
          {editError ? <p style={styles.error}>{editError}</p> : null}
          {editResult ? (
            <div style={styles.callout}>
              <div>{editResult.added.length > 0 ? `Added: ${editResult.added.join(', ')}` : 'No additions.'}</div>
              <div>{editResult.removed.length > 0 ? `Removed: ${editResult.removed.join(', ')}` : 'No removals.'}</div>
              {editResult.warnings.length > 0 ? <div>Warnings: {editResult.warnings.join('; ')}</div> : null}
            </div>
          ) : null}
          <div style={{ ...styles.toolbar, marginTop: '0.6rem' }}>
            <button type="button" style={styles.button} disabled={editSaving} onClick={() => void saveGroups(person)}>
              {editSaving ? 'Saving…' : 'Save capability groups'}
            </button>
          </div>
        </>
      )}
    </div>
  )

  const renderPanel = (person: Person) => {
    const tabButtonStyle = (active: boolean): CSSProperties | undefined => (active ? styles.button : styles.buttonGhost)
    return (
      <div style={{ display: 'grid', gap: '0.8rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', gap: '0.5rem' }} role="tablist" aria-label={`${person.displayName ?? person.email}'s panel`}>
            <button
              type="button"
              role="tab"
              aria-selected={personTab === 'access'}
              style={tabButtonStyle(personTab === 'access')}
              onClick={() => setPersonTab('access')}
            >
              Identity &amp; access
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={personTab === 'keys'}
              style={tabButtonStyle(personTab === 'keys')}
              onClick={() => setPersonTab('keys')}
            >
              Keys &amp; budgets
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={personTab === 'usage'}
              style={tabButtonStyle(personTab === 'usage')}
              onClick={() => void openUsageTab(person)}
            >
              Usage
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={personTab === 'history'}
              style={tabButtonStyle(personTab === 'history')}
              onClick={() => void openHistoryTab(person)}
            >
              Onboarding history
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={personTab === 'audit'}
              style={tabButtonStyle(personTab === 'audit')}
              onClick={() => void openAuditTab(person)}
            >
              Audit
            </button>
          </div>
          {/* Offboard (Unit 16, folded in from the retired Users page's row
              action, which nobody could find behind the "..." menu there).
              Always visible beside the tabs, not tucked inside any one of
              them, and not reversible -- so the confirm is an inline row,
              never `window.confirm`. */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            {confirmOffboard === person.email ? (
              <>
                <span style={styles.hint}>Offboard {person.email}? Removes CHU-GPT-Users and every capability group.</span>
                <button type="button" style={styles.buttonDanger} disabled={busy} onClick={() => void offboardPerson(person)}>
                  Yes, offboard
                </button>
                <button type="button" style={styles.buttonGhost} disabled={busy} onClick={() => setConfirmOffboard(null)}>
                  Cancel
                </button>
              </>
            ) : (
              <button type="button" style={styles.buttonDanger} disabled={busy} onClick={() => setConfirmOffboard(person.email)}>
                Offboard
              </button>
            )}
          </div>
        </div>
        {personTab === 'access' ? (
          <div style={{ display: 'grid', gap: '1rem' }}>
            {renderCapabilityGroups(person)}
            <PersonView tokens={tokens} email={person.email} onClear={() => setSelected(null)} />
          </div>
        ) : personTab === 'keys' ? (
          renderKeysAndBudgets(person)
        ) : personTab === 'usage' ? (
          renderUsage()
        ) : personTab === 'history' ? (
          renderHistory()
        ) : (
          renderAudit()
        )}
      </div>
    )
  }

  return (
    <div style={styles.page}>
      <div style={styles.statsRow}>
        <StatCard label="People with access" value={String(people.length)} tokens={tokens} />
        <StatCard label="Hold a developer key" value={String(withKeys)} tokens={tokens} />
        <StatCard
          label="No spend, no key"
          value={String(notStarted.length)}
          tokens={tokens}
          hint="Have access and have not started"
        />
        <StatCard
          label="Default chat ceiling"
          value={money(defaultChatBudget)}
          tokens={tokens}
          hint="Applies to anyone with no explicit budget"
        />
      </div>

      <section style={styles.section}>
        <h2 style={styles.sectionTitle}>Grant access</h2>
        <p style={styles.sectionSubtitle}>
          Adds someone to CHU-GPT-Users so they can sign in, plus any capability groups. They
          appear in the table below straight away, where you can mint them a key and set ceilings.
        </p>
        <div style={{ maxWidth: '32rem' }}>
        <DirectoryPicker
          tokens={tokens}
          disabled={busy}
          // Anyone already through the gate is hidden: this control grants
          // access, and offering it for someone who already has it invites
          // a confusing no-op rather than an obvious one.
          excludeIds={people.map((p) => p.id)}
          onPick={(u) => setPending((prev) => (prev.some((p) => p.id === u.id) ? prev : [...prev, u]))}
        />
        </div>
        {pending.length ? (
          <>
            <ul>
              {pending.map((u) => (
                <li key={u.id}>
                  {dirLabel(u)} ({dirEmail(u)}){' '}
                  <button
                    type="button"
                    onClick={() => setPending((prev) => prev.filter((p) => p.id !== u.id))}
                  >
                    Remove
                  </button>
                </li>
              ))}
            </ul>
            <fieldset>
              <legend>Capability groups</legend>
              {CAPABILITY_GROUPS.map((g) => (
                <label key={g}>
                  <input
                    type="checkbox"
                    checked={grantGroups.includes(g)}
                    onChange={(e) =>
                      setGrantGroups((prev) =>
                        e.target.checked ? [...prev, g] : prev.filter((x) => x !== g),
                      )
                    }
                  />
                  {g}
                </label>
              ))}
            </fieldset>
            <button type="button" disabled={busy} onClick={() => void grantAccess()}>
              Grant access to {pending.length} {pending.length === 1 ? 'person' : 'people'}
            </button>
          </>
        ) : null}
        {/* The per-person panel below also renders `notice`, but it only
            exists while someone is selected -- and granting access happens
            with nobody selected, so the result would otherwise be invisible. */}
        {notice && !selected ? <p role="status">{notice}</p> : null}
      </section>

      <section style={styles.section}>
        {/* Header: title and a two-line description on the left, the page's
            ACTIONS on the right. Filters get a bar of their own below. Until
            24 Sep 2026 all of it was one wrapping row, so three action
            buttons landed between filters and the row broke wherever the
            window width put it (Maroun: "I don't like the layout and
            spacing"). */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: '1rem 1.5rem',
            flexWrap: 'wrap',
            marginBottom: '1rem',
          }}
        >
          <div style={{ minWidth: 0, flex: '1 1 28rem' }}>
            <h2 style={styles.sectionTitle}>People</h2>
            <p style={{ ...styles.sectionSubtitle, margin: '0.3rem 0 0' }}>
              Everyone in CHU-GPT-Users. Chat and developer-key spend run under separate ceilings and are never a
              combined total. Select a person for their identity and access, keys and budgets.
            </p>
          </div>
          <div
            role="group"
            aria-label="People actions"
            style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}
          >
            {/* Recipients are computed server-side at send time
                (POST /api/policy/notice), never from this page's own
                policyByEmail snapshot -- see sendPolicyNotice's own comment.
                The button used to POST on its own first click, emailing
                everyone in one press with no way to see who first -- it now
                only opens a confirm naming the live count; Send is the
                second, separate click. */}
            {noticeConfirming ? (
              <span
                role="group"
                aria-label="Confirm policy notice"
                style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}
              >
                {noticeImpactError ? (
                  <span style={{ color: tokens.danger, fontSize: '0.85rem' }}>{noticeImpactError}</span>
                ) : noticeImpact === null ? (
                  <span style={{ color: tokens.textSecondary, fontSize: '0.85rem' }}>Checking who would be emailed…</span>
                ) : noticeImpact === 0 ? (
                  <span style={{ color: tokens.textSecondary, fontSize: '0.85rem' }}>
                    Nobody is missing an acceptance for policy {policyCurrentVersion ?? 'the current version'}.
                  </span>
                ) : (
                  <span style={{ color: tokens.textPrimary, fontSize: '0.85rem' }}>
                    Email {noticeImpact} {noticeImpact === 1 ? 'person' : 'people'} who have not accepted policy{' '}
                    {policyCurrentVersion ?? 'the current version'}?
                  </span>
                )}
                {noticeImpact !== null && noticeImpact > 0 ? (
                  <button type="button" style={styles.button} onClick={() => void sendPolicyNotice()} disabled={noticeBusy}>
                    {noticeBusy ? 'Sending…' : 'Send'}
                  </button>
                ) : null}
                <button type="button" style={styles.buttonGhost} onClick={cancelNoticeConfirm} disabled={noticeBusy}>
                  Cancel
                </button>
              </span>
            ) : (
              <button
                type="button"
                style={styles.buttonGhost}
                onClick={() => void openNoticeConfirm()}
                title="Emails everyone without an acceptance row for the current policy version."
              >
                Send policy notice to everyone without acceptance
              </button>
            )}
            {/* One click rather than a curl with a bearer token. Idempotent,
                and it skips anyone with an explicit budget, so a stray second
                press cannot undo a ceiling somebody raised on purpose. */}
            <button
              type="button"
              style={styles.buttonGhost}
              onClick={() => void backfillBudgets()}
              disabled={busy}
              title="Give everyone without an explicit chat budget a $100/30d one. Never lowers an existing ceiling and never resets spend."
            >
              Backfill 30-day chat budgets
            </button>
            <button type="button" style={styles.button} onClick={() => void load()} disabled={loading}>
              Refresh
            </button>
          </div>
        </div>

        <div
          role="group"
          aria-label="Filters"
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '0.75rem',
            padding: '0.9rem 1rem',
            marginBottom: '1rem',
            borderRadius: '0.75rem',
            border: `1px solid ${tokens.border}`,
            background: tokens.surfaceRaised,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '0.75rem 1.25rem' }}>
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Filter by name, email or department"
              aria-label="Filter people"
              style={{ ...styles.input, flex: '1 1 18rem', minWidth: '14rem', background: tokens.surface }}
            />
            {/* DataTable renders plain <th> text, so sorting is driven from
                here rather than by making its headers clickable -- that
                component is shared and deliberately unopinionated. */}
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}>
              <label style={styles.fieldLabel} htmlFor="people-sort">
                Sort
              </label>
              <select
                id="people-sort"
                value={sortKey}
                onChange={(e) => toggleSortTo(e.target.value as typeof sortKey)}
                style={{ ...styles.input, background: tokens.surface }}
              >
                <option value="chat">Chat spend</option>
                <option value="key">Key spend</option>
                <option value="name">Name</option>
                <option value="department">Department</option>
              </select>
              <button
                type="button"
                style={{ ...styles.buttonGhost, padding: '0.4rem 0.65rem', background: tokens.surface }}
                onClick={() => setSortAsc((v) => !v)}
                title={sortAsc ? 'Ascending' : 'Descending'}
              >
                {sortAsc ? '↑' : '↓'}
              </button>
            </span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}>
              <label style={styles.fieldLabel} htmlFor="people-department-filter">
                Department
              </label>
              <select
                id="people-department-filter"
                value={departmentFilter}
                onChange={(e) => setDepartmentFilter(e.target.value)}
                style={{ ...styles.input, background: tokens.surface }}
              >
                <option value="">All</option>
                {departments.map((d) => (
                  <option key={d} value={d}>
                    {d}
                  </option>
                ))}
              </select>
            </span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}>
              <label style={styles.fieldLabel} htmlFor="people-policy-filter">
                Policy
              </label>
              <select
                id="people-policy-filter"
                value={policyFilter}
                onChange={(e) => setPolicyFilter(e.target.value as typeof policyFilter)}
                style={{ ...styles.input, background: tokens.surface }}
              >
                <option value="any">Any</option>
                <option value="current">Accepted current version</option>
                <option value="stale">Accepted a stale version</option>
                <option value="none">Not accepted</option>
              </select>
            </span>
          </div>
          {/* Same `checkboxRow`/`accentColor` convention Onboarding.tsx and
              Settings.tsx use for every other checkbox in the console, drawn
              as chips so the four toggles read as one set. */}
          <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
            <label style={filterChip}>
              <input
                type="checkbox"
                checked={onlyNotStarted}
                onChange={(e) => setOnlyNotStarted(e.target.checked)}
                style={{ accentColor: tokens.brand }}
              />
              Not started only ({notStarted.length})
            </label>
            <label style={filterChip}>
              <input
                type="checkbox"
                checked={onlyWithKey}
                onChange={(e) => setOnlyWithKey(e.target.checked)}
                style={{ accentColor: tokens.brand }}
              />
              Has a developer key
            </label>
            <label style={filterChip}>
              <input
                type="checkbox"
                checked={onlyOverBudget}
                onChange={(e) => setOnlyOverBudget(e.target.checked)}
                style={{ accentColor: tokens.brand }}
              />
              Over key budget
            </label>
            <label
              style={filterChip}
              title="Entra has disabled these accounts, but they are still members of CHU-GPT-Users. That is usually an offboarding that stopped halfway: a leaver whose gate membership survived probably still owns a developer key and agent shares, neither of which any group removal touches."
            >
              <input
                type="checkbox"
                checked={onlyDisabled}
                onChange={(e) => setOnlyDisabled(e.target.checked)}
                style={{ accentColor: tokens.brand }}
              />
              Disabled ({disabledCount})
            </label>
          </div>
        </div>
        {error ? <p role="alert">{error}</p> : null}
        {noticeResult ? (
          <div role="status">
            <p style={{ margin: '0 0 0.4rem' }}>{noticeResult}</p>
            {noticeFailed.length > 0 ? (
              <button
                type="button"
                style={styles.button}
                disabled={noticeBusy}
                onClick={() => void sendPolicyNotice(noticeFailed)}
              >
                {noticeBusy ? 'Sending…' : `Retry the ${noticeFailed.length} that failed`}
              </button>
            ) : null}
          </div>
        ) : null}
        <DataTable
          columns={columns}
          rows={shown}
          getRowKey={(p) => p.email}
          getRowLabel={(p) => p.email}
          tokens={tokens}
          emptyLabel={loading ? 'Loading…' : 'No one has access yet.'}
          onRowActivate={open}
          isRowExpanded={(p) => p.email === selected?.email}
          renderExpanded={renderPanel}
        />
      </section>

    </div>
  )
}

/** A dollar ceiling plus one submit. Local because every use on this page is
 *  the same shape: an amount, and a button that says what it will do. */
function BudgetForm({
  styles,
  label,
  action,
  initial,
  disabled,
  onSubmit,
}: {
  styles: Record<string, CSSProperties>
  label: string
  /** The button's own words. "Save" and "Mint key" are different promises and
   *  one control should not claim to do both. */
  action: string
  initial: number
  disabled: boolean
  onSubmit: (value: number) => void
}) {
  const [value, setValue] = useState(String(initial))

  useEffect(() => {
    setValue(String(initial))
  }, [initial])

  const parsed = Number(value)
  const valid = Number.isFinite(parsed) && parsed >= 0

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        if (valid) onSubmit(parsed)
      }}
      style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end', flexWrap: 'wrap' }}
    >
      <div style={styles.field}>
        <label style={styles.fieldLabel} htmlFor={`budget-${label}`}>
          {label} ($)
        </label>
        <input
          id={`budget-${label}`}
          type="number"
          min="0"
          step="1"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          style={{ ...styles.input, width: '8rem' }}
        />
      </div>
      <button type="submit" style={styles.button} disabled={disabled || !valid}>
        {action}
      </button>
    </form>
  )
}
